import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import RegisterForm from '../components/RegisterForm';
import { useToast } from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';
import { useAuth } from '../contexts/AuthContext';
import { adminApi } from '../services/adminApi';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { AlertCircle, Ticket, CheckCircle2, Loader2 } from 'lucide-react';

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toasts, success, removeToast } = useToast();
  const { user } = useAuth();

  const searchParams = new URLSearchParams(location.search);
  const inviteToken = searchParams.get('invite_token') || undefined;
  const returnTo = searchParams.get('return_to');
  const codeParam = searchParams.get('code');
  const emailParam = searchParams.get('email');

  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [earlyAccessMode, setEarlyAccessMode] = useState(false);
  const [inviteCode, setInviteCode] = useState(codeParam || '');
  const [inviteValidated, setInviteValidated] = useState(false);
  const [inviteTier, setInviteTier] = useState<string | null>(null);
  const [validatingInvite, setValidatingInvite] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await adminApi.getPublicSettings();
        setRegistrationEnabled(settings.registrationEnabled);
        setEarlyAccessMode(settings.earlyAccessMode);
      } catch (err) {
        console.error('Failed to fetch settings:', err);
        // Default to allowing registration if settings fetch fails
      } finally {
        setSettingsLoading(false);
      }
    };

    fetchSettings();
    fetchSettings();
  }, []);

  // Auto-validate invite code if present in URL
  useEffect(() => {
    if (codeParam && !inviteValidated && !validatingInvite) {
      handleValidateInvite();
    }
  }, [codeParam, registrationEnabled]);

  const handleValidateInvite = async () => {
    if (!inviteCode.trim()) return;
    
    setValidatingInvite(true);
    setInviteError(null);
    
    try {
      const result = await adminApi.validateInviteCode(inviteCode.trim());
      if (result.valid) {
        setInviteValidated(true);
        setInviteTier(result.tier);
      }
    } catch (err: any) {
      setInviteError(err.response?.data?.error?.message || 'Invalid invite code');
    } finally {
      setValidatingInvite(false);
    }
  };

  const handleRegisterSuccess = () => {
    success('Account Created!', 'Please check your email to verify your account.');
    
    // If user was invited, go to the invite flow destination
    if (returnTo) {
      setTimeout(() => {
        navigate(returnTo, { replace: true });
      }, 1500);
      return;
    }
    
    // Get the email from auth context or sessionStorage for verification page
    const email = user?.email || sessionStorage.getItem('registerEmail') || '';
    
    setTimeout(() => {
      navigate(`/verify-email-pending?email=${encodeURIComponent(email)}`, { replace: true });
    }, 1500);
  };

  // Show loading while fetching settings
  if (settingsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#161E54]" />
      </div>
    );
  }

  // Registration is completely disabled
  if (!registrationEnabled && !earlyAccessMode) {
    return (
      <div className="min-h-screen w-full flex bg-gray-50">
        {/* Left Side - Decorative Neo */}
        <div className="hidden lg:flex w-1/2 bg-[#161E54] relative overflow-hidden items-center justify-center p-12 border-r-4 border-black">
          <div className="absolute inset-0 z-0 opacity-10">
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,#F16D34_2px,transparent_2px)] bg-[size:24px_24px]" />
          </div>

          <div className="relative z-10 max-w-lg text-[#F7F7F7] space-y-8">
            <h1 className="text-6xl font-black tracking-tighter leading-tight text-[#BBE0EF]">
              COMING<br/>
              SOON.
            </h1>
            
            <p className="text-xl text-[#FF986A] font-medium max-w-md">
              We're working hard to bring you the best retrospective experience. Stay tuned for updates!
            </p>

            <div className="grid grid-cols-2 gap-6 pt-4">
               <div className="p-6 bg-[#BBE0EF] border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] text-[#161E54]">
                  <div className="text-4xl font-black mb-1">🚀</div>
                  <div className="text-sm font-bold uppercase tracking-wide">Launching Soon</div>
               </div>
               <div className="p-6 bg-[#F16D34] border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] text-white">
                  <div className="text-4xl font-black mb-1">💡</div>
                  <div className="text-sm font-bold uppercase tracking-wide">Better Retros</div>
               </div>
            </div>
          </div>
        </div>

        {/* Right Side - Closed Message */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-12 relative bg-[#F7F7F7]">
          <div className="w-full max-w-[480px] relative z-10">
            <div className="bg-white border-4 border-[#161E54] p-8 sm:p-12 shadow-neo lg:shadow-neo-lg">
              {/* Lock Icon with Animation */}
              <div className="mb-8 text-center">
                <div className="relative inline-block">
                  <div className="w-24 h-24 bg-gradient-to-br from-[#161E54] to-[#2A3578] border-4 border-black rounded-2xl flex items-center justify-center mx-auto shadow-neo transform hover:rotate-3 transition-transform">
                    <svg 
                      className="w-12 h-12 text-[#BBE0EF]" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2.5} 
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                      />
                    </svg>
                  </div>
                  {/* Decorative dots */}
                  <div className="absolute -top-2 -right-2 w-4 h-4 bg-[#F16D34] rounded-full border-2 border-black" />
                  <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-[#BBE0EF] rounded-full border-2 border-black" />
                </div>
              </div>

              <div className="text-center mb-8">
                <h2 className="text-3xl sm:text-4xl font-black text-[#161E54] mb-4 uppercase tracking-tight">
                  Registration Closed
                </h2>
                <div className="h-2 w-20 bg-[#F16D34] mx-auto mb-6" />
                <p className="text-[#161E54] font-medium text-lg leading-relaxed">
                  We're not accepting new registrations at this time. Check back soon or sign in if you already have an account.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-4">
                <Link 
                  to="/login"
                  className="w-full h-14 bg-[#F16D34] hover:bg-[#E05D25] text-white font-black text-lg border-4 border-black shadow-neo hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all flex items-center justify-center gap-2"
                >
                  SIGN IN
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>

                <Link 
                  to="/"
                  className="w-full h-12 bg-white hover:bg-[#F7F7F7] text-[#161E54] font-bold border-4 border-[#161E54] shadow-neo-sm hover:shadow-none hover:translate-x-0.5 hover:translate-y-0.5 transition-all flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  Back to Home
                </Link>
              </div>

              {/* Contact Section */}
              <div className="mt-8 text-center bg-[#BBE0EF] border-2 border-[#161E54] p-4 shadow-neo-sm">
                <p className="text-sm text-[#161E54] font-bold">
                  Want to join the waitlist?{' '}
                  <a 
                    href="mailto:hi@rndhri.com" 
                    className="font-black text-[#F16D34] hover:underline decoration-2 underline-offset-2"
                  >
                    Contact Us
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Early access mode - require invite code first
  if (earlyAccessMode && !inviteValidated && !inviteToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <ToastContainer toasts={toasts} onClose={removeToast} />
        <div className="max-w-md w-full bg-white border-4 border-[#161E54] p-8 shadow-neo">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-[#F16D34] rounded-full flex items-center justify-center mx-auto mb-4">
              <Ticket className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-black text-[#161E54] mb-2">Early Access</h1>
            <p className="text-gray-600">
              We're in early access mode. Enter your invite code to create an account.
            </p>
          </div>

          <div className="space-y-4">
            <Input
              type="text"
              placeholder="Enter your invite code"
              value={inviteCode}
              onChange={(e) => {
                setInviteCode(e.target.value);
                setInviteError(null);
              }}
              className="h-14 text-lg font-mono text-center tracking-widest uppercase"
            />

            {inviteError && (
              <div className="flex items-center gap-2 text-red-500 text-sm">
                <AlertCircle className="w-4 h-4" />
                {inviteError}
              </div>
            )}

            <Button
              onClick={handleValidateInvite}
              disabled={!inviteCode.trim() || validatingInvite}
              className="w-full h-12 bg-[#F16D34] hover:bg-[#E05D25] font-bold"
            >
              {validatingInvite ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Validate Code'
              )}
            </Button>
          </div>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-[#161E54] font-bold hover:underline text-sm">
              Already have an account? Sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex bg-gray-50">
      <ToastContainer toasts={toasts} onClose={removeToast} />
      
      {/* Left Side - Decorative Neo */}
      <div className="hidden lg:flex w-1/2 bg-[#161E54] relative overflow-hidden items-center justify-center p-12 border-r-4 border-black">
        <div className="absolute inset-0 z-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,#F16D34_2px,transparent_2px)] bg-[size:24px_24px]" />
        </div>

        <div className="relative z-10 max-w-lg text-[#F7F7F7] space-y-8">
          <h1 className="text-6xl font-black tracking-tighter leading-tight text-[#BBE0EF]">
            JOIN THE<br/>
            MOVEMENT.
          </h1>
          
          <p className="text-xl text-[#FF986A] font-medium max-w-md">
            Create an account to facilitate engaging retrospectives, track action items, and build a culture of continuous improvement.
          </p>

          <div className="grid grid-cols-2 gap-6 pt-4">
             <div className="p-6 bg-[#BBE0EF] border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] text-[#161E54]">
                <div className="text-4xl font-black mb-1">10k+</div>
                <div className="text-sm font-bold uppercase tracking-wide">Teams Onboarded</div>
             </div>
             <div className="p-6 bg-[#F16D34] border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] text-white">
                <div className="text-4xl font-black mb-1">99%</div>
                <div className="text-sm font-bold uppercase tracking-wide">Satisfaction Rate</div>
             </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-12 relative bg-[#F7F7F7]">

        <div className="w-full max-w-[480px] relative z-10">
          <div className="bg-white border-4 border-[#161E54] p-8 sm:p-12 shadow-neo lg:shadow-neo-lg transition-transform hover:-translate-y-1">
            <div className="mb-8 text-center">
              <h2 className="text-4xl font-black text-[#161E54] mb-4 uppercase tracking-tight">Create Account</h2>
              <div className="h-2 w-20 bg-[#F16D34] mx-auto mb-6"></div>
              
              {inviteValidated && inviteTier && (
                <div className="flex items-center justify-center gap-2 text-green-600 bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-medium">
                    Invite validated! Tier: <span className="font-bold capitalize">{inviteTier.replace('_', ' ')}</span>
                  </span>
                </div>
              )}
              
              <p className="text-[#161E54] font-medium text-lg">
                It's free and takes less than a minute.
              </p>
            </div>

            <RegisterForm 
              onSuccess={handleRegisterSuccess} 
              inviteToken={inviteToken} 
              inviteCode={inviteValidated ? inviteCode.trim() : undefined}
              initialEmail={emailParam || undefined}
            />

            <div className="mt-8 text-center bg-[#BBE0EF] border-2 border-[#161E54] p-4 font-bold shadow-neo-sm">
              <p className="text-sm text-[#161E54]">
                Already have an account?{' '}
                <Link 
                  to="/login" 
                  className="font-black text-[#F16D34] hover:text-[#161E54] hover:underline decoration-4 underline-offset-4"
                >
                  SIGN IN INSTEAD
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;

